/**
 * Confirm / typed-confirm dialog styles — the shell, the pinned type-to-confirm
 * field, and the "what will happen" impact card.
 *
 * Extracted from `overlayStyles.js` (which owns the generic overlay system:
 * scrims, panels, banners, action buttons) so that file stays under the 500-line
 * cap. This is the confirmation FAMILY specifically: `ConfirmDialog`,
 * `ConfirmDialogInline`, `ConfirmImpactList`, `BulkActionProgressModal`, and the
 * admin/platform typed-confirm modals.
 *
 * Import direction is one-way — this module imports `overlayActionsRow` from
 * `overlayStyles`; `overlayStyles` must NOT import from here, or the two modules
 * form a cycle and the exported style objects can initialise as `undefined`.
 */

import { foundation as f } from './tokens';
import { overlayActionsRow } from './overlayStyles';
import { semantic } from './theme/semanticTokens';

const { color, spacePx, radii, fontSize, fontWeight, fontFamily } = f;

/** Flex column shell for confirm / typed-input modals. */
export const confirmDialogFlexShell = {
  display: 'flex',
  flexDirection: 'column',
};

/** Pinned type-to-confirm field — sits above action buttons, outside scroll body. */
export const typedConfirmInputSection = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: spacePx[2],
  marginTop: spacePx[3],
};

export const typedConfirmInputLabel = {
  fontSize: fontSize.xs,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.sans,
  color: color.neutral.textSecondary,
};

/**
 * The phrase to type, shown as a select-all chip on its own line between the
 * label and the input (RD-UI-005). No click handler by design: one click
 * selects it on desktop, press-and-hold on iPhone. red[700] text, not the
 * danger red — red600 on red[50] fails 4.5:1.
 */
export const typedConfirmPhraseChip = {
  display: 'inline-block',
  alignSelf: 'flex-start',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: `${spacePx[1]} ${spacePx[2]}`,
  borderRadius: radii.md,
  background: color.semantic.dangerFaint,
  border: `1px solid ${color.semantic.dangerBorder}`,
  color: color.semantic.dangerStrong,
  fontFamily: semantic.fontFamily.mono,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  lineHeight: 1.4,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  userSelect: 'all',
  WebkitUserSelect: 'all',
  cursor: 'text',
};

export const typedConfirmInputField = {
  width: '100%',
  padding: `${spacePx[2]} ${spacePx[3]}`,
  fontSize: fontSize.sm,
  fontFamily: fontFamily.sans,
  color: color.neutral.text,
  backgroundColor: color.neutral.bg,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.md,
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border-color 150ms ease',
};

/**
 * Impact-card visual system — the bordered "what will happen" card used by
 * confirmation modals (single delete, bulk delete). Shared so the single +
 * bulk surfaces can't drift apart. `ConfirmImpactList` and
 * `BulkActionProgressModal` both consume these.
 */
export const confirmImpactCard = {
  background: color.neutral.surface,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.lg,
  overflow: 'hidden',
};

export const confirmImpactHeader = {
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  color: color.neutral.textMuted,
  padding: `${spacePx[3]} ${spacePx[4]} ${spacePx[1]}`,
};

/** Value colour per row tone — colour only where it means something. */
export const confirmImpactTone = {
  danger: { color: color.semantic.danger, strong: true },
  success: { color: color.semantic.successIcon, strong: true },
  warning: { color: color.semantic.warning, strong: true },
  info: { color: color.neutral.textSecondary },
  neutral: { color: color.neutral.textSecondary },
};

/** Action row for confirm dialogs; extra top margin when below a typed-input field. */
export function typedConfirmActionsRow({ afterInput = false } = {}) {
  return {
    ...overlayActionsRow,
    flexShrink: 0,
    marginTop: afterInput ? spacePx[5] : spacePx[4],
  };
}
